import { memo, useMemo } from 'react';
import { Lightbulb, Clock, ArrowRight, Scissors, AlertCircle, Check, X } from 'lucide-react';
import { PLATFORMS } from '../constants';
import { formatDate, isOverdue, isSoon } from '../utils/helpers';

export const Dashboard = memo(({ content, user, move, stats }) => {
  const pending = useMemo(() =>
    content.filter(c => c.status === 'pending_approval'),
    [content]
  );

  const deadlines = useMemo(() =>
    content
      .filter(c => c.status !== 'published' && (c.deadlines?.edit || c.deadlines?.publish))
      .map(c => ({
        ...c,
        date: c.deadlines.edit || c.deadlines.publish,
        type: c.deadlines.edit ? 'Edit' : 'Publish'
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 6),
    [content]
  );

  const pendingCutdowns = useMemo(() =>
    content
      .flatMap(c =>
        (c.cutdowns || [])
          .filter(cd => cd.status !== 'done')
          .map(cd => ({ ...cd, parent: c.title }))
      )
      .slice(0, 5),
    [content]
  );

  return (
    <div className="space-y-6 max-w-5xl">
      <h2 className="text-2xl font-bold text-slate-800">Dashboard</h2>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatCard label="Ideas" value={stats.ideas} icon={Lightbulb} bg="bg-amber-100" fg="text-amber-600" />
        <StatCard label="Pending" value={stats.pending} icon={Clock} bg="bg-blue-100" fg="text-blue-600" />
        <StatCard label="Active" value={stats.active} icon={ArrowRight} bg="bg-green-100" fg="text-green-600" />
        <StatCard label="Cutdowns" value={stats.cutdowns} icon={Scissors} bg="bg-orange-100" fg="text-orange-600" />
      </div>

      {/* Overdue Alert */}
      {stats.overdue > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <p className="text-base text-red-700 font-semibold">
            {stats.overdue} item(s) overdue — check your pipeline!
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Pending Approval */}
        {user === 'zein' && (
          <PendingApprovalCard pending={pending} move={move} />
        )}

        {/* Deadlines */}
        <DeadlinesCard deadlines={deadlines} user={user} />

        {/* Pending Cutdowns */}
        {user !== 'zein' && (
          <PendingCutdownsCard cutdowns={pendingCutdowns} />
        )}
      </div>
    </div>
  );
});

Dashboard.displayName = 'Dashboard';

const StatCard = memo(({ label, value, icon: Icon, bg, fg }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
    <div className={`w-14 h-14 ${bg} rounded-xl flex items-center justify-center`}>
      <Icon className={`w-7 h-7 ${fg}`} />
    </div>
    <div>
      <p className="text-3xl font-bold text-slate-800">{value}</p>
      <p className="text-sm text-slate-500 font-medium">{label}</p>
    </div>
  </div>
));

StatCard.displayName = 'StatCard';

const PendingApprovalCard = memo(({ pending, move }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6">
    <div className="flex items-center justify-between mb-5">
      <h3 className="font-bold text-lg text-slate-800">Pending Approval</h3>
      {pending.length > 0 && (
        <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-semibold">
          {pending.length}
        </span>
      )}
    </div>
    {pending.length === 0 ? (
      <p className="text-slate-400 text-center py-8">All caught up! ✓</p>
    ) : (
      <div className="space-y-3">
        {pending.slice(0, 5).map(c => (
          <div key={c.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div>
              <p className="font-semibold text-slate-800">{c.title}</p>
              <p className="text-sm text-slate-400">from Mo</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => move(c.id, 'approved')}
                className="p-2.5 bg-green-100 text-green-600 rounded-xl hover:bg-green-200"
              >
                <Check className="w-5 h-5" />
              </button>
              <button
                onClick={() => move(c.id, 'idea')}
                className="p-2.5 bg-red-100 text-red-600 rounded-xl hover:bg-red-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
));

PendingApprovalCard.displayName = 'PendingApprovalCard';

const DeadlinesCard = memo(({ deadlines, user }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 p-6 ${user !== 'zein' ? 'col-span-1' : ''}`}>
    <h3 className="font-bold text-lg text-slate-800 mb-5">Upcoming Deadlines</h3>
    {deadlines.length === 0 ? (
      <p className="text-slate-400 text-center py-8">No deadlines set</p>
    ) : (
      <div className="space-y-3">
        {deadlines.map((c, i) => {
          const overdueStatus = isOverdue(c.date);
          const soonStatus = isSoon(c.date);

          return (
            <div
              key={i}
              className={`flex items-center justify-between p-4 rounded-xl ${
                overdueStatus ? 'bg-red-50' : soonStatus ? 'bg-amber-50' : 'bg-slate-50'
              }`}
            >
              <div>
                <p className="font-semibold text-slate-800">{c.title}</p>
                <p className={`text-sm ${overdueStatus ? 'text-red-500' : 'text-slate-400'}`}>
                  {c.type} · {formatDate(c.date)}
                </p>
              </div>
              {overdueStatus && (
                <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                  Overdue
                </span>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
));

DeadlinesCard.displayName = 'DeadlinesCard';

const PendingCutdownsCard = memo(({ cutdowns }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6">
    <div className="flex items-center justify-between mb-5">
      <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
        <Scissors className="w-5 h-5 text-orange-500" /> Cutdowns to Deliver
      </h3>
    </div>
    {cutdowns.length === 0 ? (
      <p className="text-slate-400 text-center py-8">All cutdowns delivered! ✓</p>
    ) : (
      <div className="space-y-3">
        {cutdowns.map((cd, i) => (
          <div key={i} className="p-4 bg-orange-50 rounded-xl">
            <p className="font-semibold text-slate-800">{cd.title}</p>
            <p className="text-sm text-slate-500">from &quot;{cd.parent}&quot;</p>
            <div className="flex items-center gap-2 mt-2">
              {cd.platforms?.map(p => {
                const pl = PLATFORMS.find(x => x.id === p);
                return pl && (
                  <span key={p} className={`${pl.bg} ${pl.color} text-xs px-2 py-1 rounded-full font-medium`}>
                    {pl.label}
                  </span>
                );
              })}
              <span
                className={`text-xs px-2 py-1 rounded-full font-medium ${
                  cd.status === 'editing' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'
                }`}
              >
                {cd.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
));

PendingCutdownsCard.displayName = 'PendingCutdownsCard';
